import Button from "@/components/base/Button";
import PageHero from "@/components/base/PageHero";
import ProcessFlow from "@/components/base/ProcessFlow";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import Accordion, { type FaqItem } from "@/components/base/Accordion";
import { useUi } from "@/context/UiContext";
import Seo from "@/components/base/Seo";
import { faqPageSchema } from "@/lib/seo";

const FIT_ITEMS = [
  "學過很多，但真正落實的不多",
  "常常想很多，卻很難開始",
  "開始很有熱情，但容易中斷",
  "每天很忙，卻不確定方向",
  "遇到挫折容易自我懷疑",
  "正在帶團隊，希望讓學習轉化成行動",
];

const PERSONAL = ["行動力", "拖延", "目標", "習慣", "時間", "自我管理", "人生方向"];
const BUSINESS = ["人才培育", "主管發展", "團隊執行", "目標管理", "自我管理", "學習轉化"];

const FAQ: FaqItem[] = [
  {
    q: "幸福人才和一般自我成長課程有什麼不同？",
    a: "幸福人才養成系統不只是一堂課，而是一套從信念、行動到人生方向整合的方法。它不要求你更努力，而是先幫你看清楚自己卡在哪裡，再從做得到的地方開始，逐步建立能持續運轉的成長方式。",
  },
  {
    q: "這是不是時間管理課？",
    a: "不是。時間管理只是行動系統中可能運用的一環。幸福人才養成系統更完整地看待「為什麼做不到」，從信念、行動與人生方向三個面向一起著手。",
  },
  {
    q: "沒有明確目標也能開始嗎？",
    a: "可以。很多人正是因為還沒有明確目標才卡住。系統會先從理解自己現在的位置開始，在過程中逐步釐清方向。",
  },
  {
    q: "適合企業嗎？",
    a: "適合。幸福人才養成系統同時應用於個人成長與企業人才培育，包含主管發展、團隊執行、目標管理與學習轉化等議題。",
  },
];

function Arrow() {
  return (
    <div className="flex items-center justify-center py-1">
      <i className="ri-arrow-down-line lg:hidden text-gold text-xl" aria-hidden="true"></i>
      <i className="ri-arrow-right-line hidden lg:block text-gold text-xl" aria-hidden="true"></i>
    </div>
  );
}

export default function HappinessTalentSystem() {
  const { openQuiz, openAi } = useUi();

  return (
    <>
      <Seo
        title="幸福人才養成系統｜從知道到做到，再到持續做到｜許文彰"
        description="幸福人才養成系統由信念系統、行動系統、人生系統三大核心與能力升級引擎構成，透過五階成長路徑（覺醒、破圈、建系、續航、影響），幫助你從知道到做到、再到持續做到。"
        keywords="幸福人才養成系統,信念系統,行動系統,人生系統,五階成長,許文彰"
        jsonLd={faqPageSchema(FAQ)}
      />
      <PageHero
        eyebrow="幸福人才養成系統"
        title={
          <>
            真正的改變，
            <br />
            不是知道更多，而是能夠持續做到。
          </>
        }
        description={
          <>
            <p>
              幸福人才養成系統不是另一套要求你「更努力」的方法。它從信念、行動與
              人生方向三個面向出發，幫助你理解：我為什麼卡住？我應該從哪裡開始？
              如何讓改變不再只是短期熱情？
            </p>
          </>
        }
      >
        <Button onClick={openQuiz} icon="ri-arrow-right-line">
          30 秒找出我的行動卡點
        </Button>
      </PageHero>

      {/* 為什麼需要一套系統 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="為什麼需要系統"
              title="我們真正缺少的，常常不是答案。"
            />
            <div className="mt-8 rounded-card bg-cream border border-line p-8 md:p-10">
              <p className="text-lg md:text-xl font-semibold text-primary leading-[1.6]">
                幸福人才真正想處理的不是「你還缺什麼知識？」，而是「是什麼讓你一直沒有做到？」
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 什麼是幸福人才 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="定義"
              title="幸福人才，不只是「很厲害的人」。"
            />
            <div className="mt-8 rounded-card bg-primary-dark p-8 md:p-10">
              <p className="text-lg md:text-2xl font-bold text-white leading-[1.55]">
                幸福人才，是能理解自己、採取行動、持續成長，並把能力用在真正重要地方的人。
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 三大核心系統 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="三大核心系統" title="改變，從三個互相連結的面向開始。" />
          </Reveal>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-4 lg:gap-3 items-stretch">
            <Reveal>
              <div className="h-full rounded-card bg-cream border border-line p-7">
                <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary/10">
                  <i className="ri-compass-3-line text-2xl text-primary" aria-hidden="true"></i>
                </div>
                <h3 className="mt-5 text-lg font-bold text-ink">
                  我怎麼看待自己，會影響我怎麼行動。
                </h3>
                <p className="mt-2 text-xs font-semibold text-gold uppercase tracking-wide">
                  信念系統
                </p>
                <p className="mt-3 text-sm leading-[1.8] text-ink-soft">
                  看見原本自動運作的想法，增加新的選擇。
                </p>
              </div>
            </Reveal>

            <Arrow />

            <Reveal delay={80}>
              <div className="h-full rounded-card bg-primary text-white p-7">
                <div className="w-12 h-12 flex items-center justify-center rounded-md bg-gold/20">
                  <i className="ri-rocket-2-line text-2xl text-gold" aria-hidden="true"></i>
                </div>
                <h3 className="mt-5 text-lg font-bold">
                  改變必須落到每天真正做得到的事情。
                </h3>
                <p className="mt-2 text-xs font-semibold text-gold uppercase tracking-wide">
                  行動系統
                </p>
                <p className="mt-4 text-sm leading-[1.8] text-white/85">
                  目標 → 行動 → 回饋 → 調整 → 再行動
                </p>
              </div>
            </Reveal>

            <Arrow />

            <Reveal delay={160}>
              <div className="h-full rounded-card bg-cream border border-line p-7">
                <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary/10">
                  <i className="ri-road-map-line text-2xl text-primary" aria-hidden="true"></i>
                </div>
                <h3 className="mt-5 text-lg font-bold text-ink">
                  努力之前，要先知道自己想往哪裡走。
                </h3>
                <p className="mt-2 text-xs font-semibold text-gold uppercase tracking-wide">
                  人生系統
                </p>
                <p className="mt-3 text-sm leading-[1.8] text-ink-soft">
                  讓行動逐漸形成自己真正想過的人生。
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 能力升級引擎 */}
      <section className="bg-primary-dark">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="能力升級引擎"
              title="能力，是讓三大系統持續升級的引擎。"
              dark
            />
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-10">
              <ProcessFlow
                dark
                steps={[
                  { title: "學習" },
                  { title: "實踐" },
                  { title: "回饋" },
                  { title: "調整" },
                  { title: "能力升級" },
                  { title: "再行動" },
                ]}
              />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-10 rounded-card bg-white/5 border border-white/10 p-7">
              <p className="text-base md:text-lg font-semibold text-gold-soft leading-[1.6]">
                能力不是聽懂之後得到，而是在一次次實踐與修正中長出來。
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 五階成長 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="五階成長" title="成長，會經過哪些階段？" />
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-10">
              <ProcessFlow
                steps={[
                  { title: "覺醒", subtitle: "看見" },
                  { title: "破圈", subtitle: "改變" },
                  { title: "建系", subtitle: "建立" },
                  { title: "續航", subtitle: "持續" },
                  { title: "影響", subtitle: "擴大" },
                ]}
              />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-10 text-center text-sm font-medium text-ink-soft tracking-widest">
              看見 → 改變 → 建立 → 持續 → 影響
            </p>
          </Reveal>
        </div>
      </section>

      {/* 整體架構 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="整體架構" title="一套系統，三個核心問題。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                tag: "五階成長路徑",
                q: "成長會經過哪些階段？",
                desc: "從看見，到改變、建立、持續，再到影響。",
              },
              {
                tag: "三大系統",
                q: "我要從哪些面向建立自己？",
                desc: "信念、行動與人生方向，三個互相連結的面向。",
              },
              {
                tag: "能力升級引擎",
                q: "如何在實踐中不斷進步？",
                desc: "在一次次實踐與修正中，讓能力持續升級。",
              },
            ].map((item, i) => (
              <Reveal key={item.tag} delay={i * 90}>
                <div className="h-full rounded-card bg-white border border-line p-7">
                  <span className="inline-flex rounded-full bg-gold-soft/60 px-3 py-1 text-xs font-semibold text-primary">
                    {item.tag}
                  </span>
                  <p className="mt-5 text-lg font-bold text-ink">{item.q}</p>
                  <p className="mt-3 text-sm leading-[1.8] text-ink-soft">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 適合誰 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="適合誰" title="如果你有這些感覺，也許可以從這裡開始。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FIT_ITEMS.map((item, i) => (
              <Reveal key={item} delay={i * 60}>
                <div className="flex items-start gap-3 rounded-md border border-line bg-cream p-5">
                  <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-gold/20">
                    <i className="ri-check-line text-gold" aria-hidden="true"></i>
                  </span>
                  <p className="text-sm leading-[1.7] text-ink">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 個人與企業應用 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-card bg-white border border-line p-8 flex flex-col">
                <h3 className="text-xl font-bold text-ink">個人</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {PERSONAL.map((p) => (
                    <span key={p} className="rounded-full bg-cream border border-line px-3 py-1.5 text-sm text-ink-soft">
                      {p}
                    </span>
                  ))}
                </div>
                <div className="mt-8 pt-6 border-t border-line mt-auto">
                  <Button to="/personal-growth" variant="outline" icon="ri-arrow-right-line">
                    探索個人成長
                  </Button>
                </div>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="h-full rounded-card bg-primary text-white p-8 flex flex-col">
                <h3 className="text-xl font-bold">企業</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {BUSINESS.map((p) => (
                    <span key={p} className="rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-sm text-white/85">
                      {p}
                    </span>
                  ))}
                </div>
                <div className="mt-8 pt-6 border-t border-white/15 mt-auto">
                  <Button to="/corporate-training" variant="outline-light" icon="ri-arrow-right-line">
                    了解企業與團隊培訓
                  </Button>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="常見問題" title="關於幸福人才養成系統，你可能想知道。" />
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-8 max-w-3xl">
              <Accordion items={FAQ} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary-dark">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-2xl md:text-[2.125rem] font-bold leading-[1.4] text-white">
                從知道，到做到；
                <br />
                再把做到，變成可以持續的自己。
              </h2>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={openQuiz} icon="ri-arrow-right-line">
                  30 秒找出我的行動卡點
                </Button>
                <Button onClick={openAi} variant="outline-light">
                  和 AI 幸福人才顧問聊聊
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}